import { isPlatformBrowser } from '@angular/common';
import type { Provider, ValueProvider } from '@angular/core';
import {
	DOCUMENT,
	ErrorHandler,
	inject,
	InjectionToken,
	PLATFORM_ID,
	provideAppInitializer,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import type { ApmBase } from '@elastic/apm-rum';
import { ApmErrorHandler, ApmService } from '@elastic/apm-rum-angular';
import { filter } from 'rxjs/operators';

import { normalizeTransactionPath } from '@valk-nx/core/lib/ssr/apm';

export const APM_ENVIRONMENT = new InjectionToken<ApmEnvironmentRequirements>(
	'Apm environment',
);
export interface ApmEnvironmentRequirements {
	production: boolean;
	origin: string;
	environment: string;
	name: string;
	version: string;
	/**
	 * Name transactions after the requested path. An app whose routes are a
	 * single catch-all gives the router integration no pattern to report, so
	 * every page would otherwise be filed under one name.
	 */
	nameTransactionsByPath?: boolean;
}

export function provideApmEnvironment(
	environment: ApmEnvironmentRequirements,
): ValueProvider {
	return { provide: APM_ENVIRONMENT, useValue: environment };
}
/**
 * The Angular integration names each route change after the matched route
 * config, overwriting it on `NavigationEnd`. Re-setting it there is the only
 * point that sticks: the payload freezes the name when the transaction ends,
 * and our subscriber runs after the integration's own.
 */
function nameTransactionsByPath(apm: ApmBase, router: Router): void {
	router.events
		.pipe(filter((event) => event instanceof NavigationEnd))
		.subscribe((event) => {
			const transaction = apm.getCurrentTransaction();

			if (transaction) {
				transaction.name = normalizeTransactionPath(
					event.urlAfterRedirects,
				);
			}
		});
}

export function apmInitializerFactory(
	document: Document,
	platformId: Parameters<typeof isPlatformBrowser>[0],
	apmService: ApmService,
	environment: ApmEnvironmentRequirements,
	router: Router | null,
) {
	return () => {
		if (isPlatformBrowser(platformId)) {
			if (environment.production) {
				// The path, not the href: a query string carries no page
				// identity, so naming by href files one page under a separate
				// transaction group per campaign parameter.
				const pathname = document.defaultView?.location.pathname;

				const apm = apmService.init({
					environment: environment.environment,
					pageLoadTransactionName: pathname
						? normalizeTransactionPath(pathname)
						: undefined,
					serverUrl: 'https://apm.newstory.cloud',
					serviceName: environment.name,
					serviceVersion: environment.version,
				});

				apm.addLabels({ group: 'valk' });

				if (environment.nameTransactionsByPath && router) {
					nameTransactionsByPath(apm, router);
				}
			}
		}
	};
}
export function provideApmHandling(): Provider {
	return [
		ApmService,
		{
			provide: ErrorHandler,
			useClass: ApmErrorHandler,
		},
		provideAppInitializer(
			/* istanbul ignore next */ () => {
				const initializerFn = apmInitializerFactory(
					inject(DOCUMENT),
					inject(PLATFORM_ID),
					inject(ApmService),
					inject(APM_ENVIRONMENT),
					inject(Router, { optional: true }),
				);
				return initializerFn();
			},
		),
	];
}
